/* eslint-disable max-lines */
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useRef,
  TransitionEvent,
  TouchEvent,
  useLayoutEffect,
  useState,
} from 'react'

import { useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import { goBackAction } from 'actions/route/routeAction'
import { TOUCH_DIFF_SHIFT_Y } from 'common/constants'
import { NumberDirection } from 'common-constants/numbers'
import { translateX } from 'functions/layout/translate'

import { PhotoViewerArrow } from './PhotoViewerArrow'
import { carouselDomId } from './PhotoViewerCarousel.constants'
import { usePhotoViewerContext } from './PhotoViewerCarousel.context'
import { PhotoViewerPromoCase } from './PhotoViewerCarousel.types'
import {
  enableAnimation,
  enablePointerEvents,
} from './PhotoViewerSwiper.functions'
import { usePhotoViewerSettings } from './usePhotoViewerSettings'
import { deltaMinSwipe, deltaMinTime } from '../constants'
import { calculateTranslate } from '../function/calculateTranslate'
import { PhotoViewerContainer } from '../PhotoViewer.styled'
import { photoViewerNoticeDataName } from '../PhotoViewerNotice/PhotoViewerNotice.constants'

/**
 * В кнопке тоже есть transition, отрабатывает в onTransitionEnd.
 * Нужно для дальнейшего игнора.
 * @returns
 */
const isButtonElement = (element: Element) =>
  element.tagName.toLowerCase() === 'button'

const handleSwipe = (handler: (event: TouchEvent) => void) => (event) => {
  if (isButtonElement(event.target)) {
    return false
  }

  handler(event)
}

const dataName = 'photoviewer-wrapper'

/**
 * Отвечает за свайп (драг-енд-дроп) контейнера.
 * Сообщает о завершении и изменении (-1, 0, 1),
 * т.е. влево, там же, вправо.
 * @returns
 */
export const PhotoViewerSwiper: FC<
  {
    singlePhoto: boolean
    promoCase: PhotoViewerPromoCase | null
    onUpdateView: (direction: number) => void
    onMovePromoX: (value: number) => void
    onPromoAnimation: (value: boolean) => void
    onShowPromo: (value: boolean) => void
    onUpdatePromoCase: (value: PhotoViewerPromoCase | null) => void
  } & PropsWithChildren
> = ({
  promoCase,
  singlePhoto,
  children,
  onUpdateView,
  onMovePromoX,
  onPromoAnimation,
  onShowPromo,
  onUpdatePromoCase,
}) => {
  const dispatch = useDispatch()
  const { translateX: themeTranslateX, multiplier } = useTheme()
  const { showArrows } = usePhotoViewerContext()
  // console.log('PhotoViewerSwiper', { promoCase })

  const carouselRef = useRef<HTMLDivElement>(null)
  const directionRef = useRef<number>(0)
  const timeRef = useRef<number>(0)
  const animatingRef = useRef<boolean>(false)

  /**
   * Задаем стартовую позицию промки.
   */
  useLayoutEffect(() => {
    onShowPromo(
      [
        PhotoViewerPromoCase.next,
        PhotoViewerPromoCase.nextFinished,
        PhotoViewerPromoCase.prevoius,
        PhotoViewerPromoCase.prevoiusFinished,
      ].includes(promoCase!)
    )

    switch (promoCase) {
      case PhotoViewerPromoCase.next:
        return onMovePromoX(carouselRef.current!.offsetWidth * multiplier)

      case PhotoViewerPromoCase.prevoius:
        return onMovePromoX(-carouselRef.current!.offsetWidth * multiplier)

      case PhotoViewerPromoCase.prevoiusFinished:
      case PhotoViewerPromoCase.nextFinished:
        return onMovePromoX(0)
    }
  }, [multiplier, onMovePromoX, onShowPromo, promoCase])

  const moveCarouselX = useCallback((value: number) => {
    if (carouselRef.current) {
      translateX(carouselRef.current, value)
    }
  }, [])

  const movementDataRef = useRef<{
    startX: number
    startY: number
    diffX: number
    diffY: number
    /** Стартовое направление движения, нужно для определения промки */
    direction: null | number
  }>({
    startX: 0,
    startY: 0,
    diffX: 0,
    diffY: 0,
    direction: null,
  })

  const resetToDefault = useCallback(() => {
    // console.log('reset to default', { promoCase })
    enableAnimation(carouselRef.current!, false)
    enablePointerEvents(carouselRef.current!, true)
    onPromoAnimation(false)

    directionRef.current = 0
    animatingRef.current = false
    setShowControls(true)

    movementDataRef.current = {
      startX: 0,
      startY: 0,
      diffX: 0,
      diffY: 0,
      direction: null,
    }
  }, [onPromoAnimation])

  const scrollToNextPhoto = useCallback(
    (direction: NumberDirection): void => {
      // console.log('scrollToNextPhoto', {
      //   direction,
      //   animation: animatingRef.current,
      //   dataDirection: movementDataRef.current.direction,
      // })

      if (animatingRef.current) {
        return console.warn('Can not scroll to next photo, animation is active')
      }

      animatingRef.current = true
      directionRef.current = direction

      const updatedX = calculateTranslate(
        direction,
        themeTranslateX,
        carouselRef.current!.offsetWidth
      )

      /** Анимации по завершению свайпа */
      if (
        promoCase === PhotoViewerPromoCase.next &&
        movementDataRef.current.direction! === NumberDirection.left * multiplier
      ) {
        onPromoAnimation(true)
        onMovePromoX(carouselRef.current!.offsetWidth * multiplier + updatedX)
      } else if (
        promoCase === PhotoViewerPromoCase.prevoiusFinished &&
        movementDataRef.current.direction === NumberDirection.right * multiplier
      ) {
        onPromoAnimation(true)
        onMovePromoX(updatedX)
      } else {
        enableAnimation(carouselRef.current!, true)
        moveCarouselX(updatedX)
      }
    },
    [
      moveCarouselX,
      multiplier,
      onMovePromoX,
      onPromoAnimation,
      promoCase,
      themeTranslateX,
    ]
  )

  /** Обновление кейса промо при начале движения */
  const updatePromoOnMove = useCallback(() => {
    if (
      movementDataRef.current.direction ===
        NumberDirection.right * multiplier &&
      promoCase === PhotoViewerPromoCase.next
    ) {
      onUpdatePromoCase(PhotoViewerPromoCase.prevoius)
    }
  }, [multiplier, onUpdatePromoCase, promoCase])

  const handleTouchStart = handleSwipe((event: TouchEvent) => {
    if (carouselRef.current!.style.pointerEvents === 'none') {
      /** https://youtrack.mamba.ru/issue/M-10327 */
      console.warn('Spam swipe blocked')
      return
    }
    // console.log('handleTouchStart', carouselRef.current!.style.pointerEvents)
    const touch = event.changedTouches[0]

    movementDataRef.current.startX = touch.clientX
    movementDataRef.current.startY = touch.clientY

    enableAnimation(carouselRef.current!, false)
    enablePointerEvents(carouselRef.current!, false)
    onPromoAnimation(false)
    timeRef.current = Date.now()
  })

  const handleTouchMove = handleSwipe((event: TouchEvent) => {
    const { clientX, clientY } = event.changedTouches[0]
    const { startX, startY } = movementDataRef.current

    const diffX = clientX - startX
    const diffY = clientY - startY

    movementDataRef.current.diffX = diffX
    movementDataRef.current.diffY = diffY

    /**
     * В if'е определяем стартовое направление движения,
     * чтобы понять откуда куда.
     */
    if (movementDataRef.current.direction === null) {
      movementDataRef.current.direction = diffX > 0 ? 1 : -1

      if (singlePhoto) {
        return updatePromoOnMove()
      }
    }

    if (
      promoCase === PhotoViewerPromoCase.next &&
      movementDataRef.current.direction === NumberDirection.left * multiplier
    ) {
      return onMovePromoX(carouselRef.current!.offsetWidth * multiplier + diffX)
    } else if (
      promoCase === PhotoViewerPromoCase.prevoiusFinished &&
      movementDataRef.current.direction === NumberDirection.right * multiplier
    ) {
      return onMovePromoX(diffX)
    }

    moveCarouselX(diffX)
  })

  const handleTouchEnd = handleSwipe(() => {
    const currentTime = Date.now()
    const { diffX, diffY } = movementDataRef.current
    const deltaForEasySwipe =
      currentTime - timeRef.current < deltaMinTime &&
      Math.abs(diffX) > deltaMinSwipe
    /** Полэкрана слишком много, можно сравнить с любым другим продуктом */
    const deltaSwipeX = Math.abs(diffX) > carouselRef.current!.offsetWidth / 4

    /**
     * Если сильно смахнул вниз или вверх,
     * то закрываем.
     */
    if (Math.abs(diffY) > TOUCH_DIFF_SHIFT_Y) {
      dispatch(goBackAction())
      return
    }

    if (Math.abs(diffX) === 0) {
      resetToDefault()
      return
    }

    if (deltaForEasySwipe || deltaSwipeX) {
      if (diffX > 0) {
        scrollToNextPhoto(NumberDirection.left * multiplier)
      } else {
        scrollToNextPhoto(NumberDirection.right * multiplier)
      }
    } else {
      scrollToNextPhoto(NumberDirection.none)
    }
  })

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (
        isButtonElement(event.target as Element) ||
        (event.target as Element).tagName.toLowerCase() === 'a'
      ) {
        /** Не нужны лишние срабатывания на кнопке */
        return
      }

      // console.log('handle transition end', {
      //   direction: directionRef.current,
      //   target: event.target,
      //   currentTarget: event.currentTarget,
      // })
      const div = event.target as HTMLDivElement

      if (div.dataset.name === dataName) {
        // console.log('photo swipe ended', directionRef.current)
        if (animatingRef.current) {
          if (
            promoCase === PhotoViewerPromoCase.prevoius &&
            directionRef.current === NumberDirection.left
          ) {
            onUpdatePromoCase(PhotoViewerPromoCase.prevoiusFinished)
          } else if (singlePhoto) {
            if (
              [
                PhotoViewerPromoCase.prevoiusFinished,
                PhotoViewerPromoCase.nextFinished,
              ].includes(promoCase!)
            ) {
              onUpdatePromoCase(PhotoViewerPromoCase.next)
            }
          } else {
            // console.log(
            //   'Photo transition end case not implemented',
            //   promoCase,
            //   directionRef.current
            // )
          }

          onUpdateView(directionRef.current)
          resetToDefault()
        } else {
          console.warn('Attempt to update view before transition ended')
        }
      } else if (div.dataset.name === photoViewerNoticeDataName) {
        // console.log('notice swipe ended', directionRef.current, { promoCase })
        if (directionRef.current !== NumberDirection.none) {
          if (promoCase === PhotoViewerPromoCase.next) {
            onUpdatePromoCase(PhotoViewerPromoCase.nextFinished)
          } else if (
            promoCase === PhotoViewerPromoCase.prevoiusFinished &&
            directionRef.current ===
              NumberDirection.left /** multiplier не нужен */
          ) {
            if (singlePhoto) {
              onUpdatePromoCase(PhotoViewerPromoCase.next)
            } else {
              onUpdatePromoCase(null)
            }
          } else {
            // console.log(
            //   'Notice transition end case not implemented',
            //   promoCase,
            //   directionRef.current
            // )
          }
        }
        resetToDefault()
      }
    },
    [promoCase, singlePhoto, onUpdateView, resetToDefault, onUpdatePromoCase]
  )

  const [showControls, setShowControls] = useState<boolean>(true)

  /** Стелочки и клавиатура */
  const animatedScrollToNextPhoto = useCallback(
    (direction: NumberDirection) => {
      /** Чтобы особо быстрые пользователи не ломали анимацию */
      if (animatingRef.current) {
        return console.warn('Prevent spam arraw or keyboard action')
      }

      movementDataRef.current.direction = -direction * multiplier
      updatePromoOnMove()
      enableAnimation(carouselRef.current!, true)
      enablePointerEvents(carouselRef.current!, false)
      requestAnimationFrame(() => scrollToNextPhoto(direction))
    },
    [multiplier, scrollToNextPhoto, updatePromoOnMove]
  )

  usePhotoViewerSettings(animatedScrollToNextPhoto)

  return (
    <PhotoViewerContainer
      id={carouselDomId}
      ref={carouselRef}
      data-name={dataName}
      draggable={false}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onTransitionEnd={handleTransitionEnd}
    >
      {children}

      {showArrows && (
        <PhotoViewerArrow
          isShowArrow={showControls}
          onScrollToNextPhoto={animatedScrollToNextPhoto}
        />
      )}
    </PhotoViewerContainer>
  )
}
